import { useScroll } from 'hooks'
import { useRouter } from 'next/router'
import React, { useEffect, useMemo, useRef, useState } from 'react'
import { randomNo } from 'utils'

import {
  FrontendDifficultyOptions,
  usePaginatedAllFrontendChallenges
} from '../../AlgoEditor'
import Challenges from '../Components/Challenges'

const FrontendChallenges = () => {
  const router = useRouter()
  const [selectedDifficulties, setSelectedDifficulties] = useState<string[]>([])
  const scrollContainerRef = useRef<HTMLDivElement>(null)
  const { top: scrollTop } = useScroll(scrollContainerRef)

  const {
    data: paginatedFrontendChallenges,
    fetchNextPage,
    isLoading,
    isFetchingNextPage
  } = usePaginatedAllFrontendChallenges({
    pageSize: 40,
    options: {
      topic: selectedDifficulties.join(',')
    }
  })

  const challenges = useMemo(() => {
    if (paginatedFrontendChallenges?.pages) {
      return paginatedFrontendChallenges.pages.flatMap(
        (page) => page.challenges
      )
    }
    return []
  }, [paginatedFrontendChallenges])

  useEffect(() => {
    if (!scrollContainerRef.current) return
    const { scrollHeight, clientHeight } = scrollContainerRef.current

    if (scrollHeight - scrollTop <= clientHeight + 100 && !isFetchingNextPage) {
      fetchNextPage()
    }
  }, [scrollTop, fetchNextPage, isFetchingNextPage])

  const onFeelingLucky = () => {
    if (!challenges) return

    const maxIndex = challenges.length
    const randomIdx = randomNo(0, maxIndex)

    const frontendChallenge = challenges[randomIdx]

    if (!frontendChallenge) return
    router.push(
      `/frontend-challenges/${frontendChallenge.topic}/${frontendChallenge.slug}/${frontendChallenge.id}`
    )
  }

  return (
    <Challenges
      key={'Frontend'}
      leaderboardType={'Frontend'}
      showTopics={false}
      showCompany={false}
      questions={challenges}
      difficultyOptions={FrontendDifficultyOptions}
      selectedDifficulties={selectedDifficulties}
      onFeelingLucky={onFeelingLucky}
      setSelectedDifficulties={setSelectedDifficulties}
      tableRef={scrollContainerRef}
      isFetchingNextPage={isFetchingNextPage}
      isLoading={isLoading}
    />
  )
}

FrontendChallenges.seo = {
  title: 'Solve Frontend Questions - Devsnest',
  description:
    'Solve challenging Frontend (HTML, CSS, JS, React) questions that have been asked by top tech companies to improve your coding abilities and get ready for interviews.',
  canonical: 'https://devsnest.in/algo-challenges',
  openGraph: {
    url: 'https://devsnest.in/frontend-challenges'
  }
}
export default FrontendChallenges
